<template>
  <div class="w-full h-auto p-5 flex justify-center">

    <div class="w-full max-w-3xl">

      <div
        class="text-orange-organization inline-flex flex-row justify-start items-center cursor-pointer"
        @click="goBack()"
      >
        <svg
          id="chevron-left"
          xmlns="http://www.w3.org/2000/svg"
          class="h-4 w-4 text-gray-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 19l-7-7 7-7"
          />
        </svg>
        <span class="ml-2">
          Voltar para meus atendimentos
        </span>
      </div>

      <div class="w-full mt-4">
        <ExternalOrderItemList
          v-if="ticketId"
          :ticket-id="ticketId"
          :status="status"
          :organization="organization"
        />
      </div>

      <div class="w-full relative block sm:hidden mt-5">
        <button
          id="get-attachments"
          class="w-full flex flex-row justify-center items-center py-3 text-white bg-purple-organization hover:bg-purple-organization-light active:bg-purple-organization-dark transition-all rounded shadow disabled:cursor-not-allowed disabled:bg-purple-organization disabled:opacity-50"
          @click.stop.prevent="toggleAttachmentsDropdownMenu"
          :disabled="!attachments || !attachments.length"
          :title="!attachments || !attachments.length ? 'Este atendimento não possui anexos' : ''"
        >
          <span
            class="font-medium"
          >
            {{ getAttachmentsLabel }}
          </span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5 ml-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
            />
          </svg>
        </button>
        <div
          id="attachments-dropdown-menu-container"
          class="w-full absolute top-8 flex flex-row justify-center items-center"
        >
          <div
            id="attachments-dropdown-menu"
            v-show="ticketAttachmentDropdown"
            ref="attachmentsDropdownMenu"
            class="w-11/12 flex flex-col justify-start items-start py-2 bg-white rounded shadow-lg border z-20 mx-auto"
          >
            <p
              v-for="(attachment, index) in attachments"
              :id="`ticket-attachment-${index}`"
              :key="`ticket-attachment-${index}`"
              class="px-4 py-4 inline-flex text-sm text-blue-500 underline cursor-pointer hover:text-blue-800 truncate"
              :title="attachment.filename | formatFileName"
              @click="openAttachment(attachment._id.toString())"
            >
              {{ attachment.filename | formatFileName }}
            </p>
          </div>
        </div>
      </div>

      <div class="w-full block sm:hidden mt-5">
        <button
          id="get-emails-comments-report"
          class="w-full flex flex-row justify-center items-center py-3 text-white bg-purple-organization hover:bg-purple-organization-light active:bg-purple-organization-dark transition-all rounded shadow"
          @click="generateEmailCommentsReport()"
        >
          <span
            class="font-medium"
          >
            Baixar histórico de e-mails
          </span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5 ml-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </button>
      </div>

      <div class="w-full mt-5">
        <ExternalTicketUpdateList
          v-if="ticketId"
          :ticket-id="ticketId"
          :organization="organization"
          @attachments-loaded="attachments = $event"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ExternalOrderItemList from '@/components/ExternalOrderItemList'
import ExternalTicketUpdateList from '@/components/ExternalTicketUpdateList'
import { getHashInfo } from '@/repositories/integrations/mobly/services/csExternal/hashInfo'
import {
  findOne as TicketFindOne,
  generateReportEmails as ticketGenerateReportEmails
} from '@/repositories/integrations/mobly/services/csExternal/ticket'
import { findOne as UploadFindOne } from '@/repositories/integrations/mobly/services/csExternal/upload'
import { findByPathAndDocument } from '@/repositories/integrations/mobly/services/csExternal/customer'
import { MOBLY } from '@/utils/possible-organizations-values'
import { setTheme } from '@/utils/theme'

export default {
  name: 'ExternalTicketDetail',
  components: {
    ExternalOrderItemList,
    ExternalTicketUpdateList
  },
  data () {
    return {
      ticketId: null,
      document: null,
      status: null,
      attachments: [],
      organization: null,
      type: 'customer',
      hash: null,
      snackBar: {
        show: false,
        position: 'left',
        message: '',
        duration: 3000
      },
      loading: false,
      ticketAttachmentDropdown: false
    }
  },
  computed: {
    getAttachmentsLabel () {
      return this.attachments.length > 0
        ? `Baixar anexos (${this.attachments.length})`
        : 'Baixar anexos (0)'
    }
  },
  filters: {
    formatFileName: value => (value.split('] ')[1])
  },
  mounted () {
    this.setTicket()
  },
  destroyed () {
    window.removeEventListener('click', this.closeTicketAttachmentsMenu)
  },
  methods: {
    async setTicket () {
      try {
        this.hash = encodeURIComponent(this.$route.query.h)
        this.organization = this.$route.query.org || MOBLY
        setTheme(this.organization)

        const ticketIdFromQuery = this.$route.query.t

        if (ticketIdFromQuery) {
          // Navigation from ticket list — ticketId comes from URL, hash is the original session hash
          this.ticketId = ticketIdFromQuery
          try {
            const decrypted = await this.decrypt()
            this.document = decrypted.client || decrypted.document
            this.type = decrypted.type || 'customer'
          } catch (e) {
            this.type = 'customer'
          }
        } else {
          // Direct link (email) — hash contains all ticket info
          const decrypted = await this.decrypt()
          this.ticketId = decrypted.ticket
          this.document = decrypted.client
          this.status = decrypted.status
          this.organization = decrypted.organization || MOBLY
          this.type = decrypted.type || 'customer'
          setTheme(this.organization)
        }

        if (this.organization === MOBLY && this.type !== 'lead') {
          try {
            const statusCustomerResponse = await findByPathAndDocument(this.document, this.organization)
            const statusCustomer = statusCustomerResponse.data
            const { status } = statusCustomer

            if (!status) {
              this.$router.push('/notFound')
              return
            }

            if (status === 'blocked' || status === 'inactive') {
              this.$router.push('/blocked')
              return
            }
          } catch (e) {
            console.error(e)
          }
        }
      } catch (err) {
        this.snackBar.message = 'Não foi possível abrir a página requisitada'
        this.snackBar.show = true
      }
    },

    async decrypt () {
      const hash = this.$route.query.h
      this.hash = encodeURIComponent(hash)
      const organization = this.$route.query.org
      const request = await getHashInfo({ hash }, organization)
      const decrypted = request.data
      return decrypted
    },

    async goBack () {
      this.$router.push(`/ticketList?h=${this.hash}&org=${this.organization}`)
    },

    async generateEmailCommentsReport () {
      try {
        this.snackBar.message = 'Gerando relatório de comentários'
        this.snackBar.show = true

        const ticketCod = await TicketFindOne(this.ticketId, this.organization)

        const commentsResponse = await ticketGenerateReportEmails(this.ticketId,
          { ticketCod: ticketCod.data.cod }, this.organization
        )

        const buf = Buffer.from(commentsResponse.data, 'base64')
        const pdfUrl = window.URL.createObjectURL(new Blob([buf], { type: 'application/pdf' }))
        window.open(pdfUrl, '_blank')
        setTimeout(() => window.URL.revokeObjectURL(pdfUrl), 100)
      } catch (error) {
        this.snackBar.message = error.message || 'Erro inesperado ocorreu ao gerar o relatório'
        this.snackBar.show = true
      }
    },

    async openAttachment (attachmentId) {
      if (this.loading) return
      try {
        this.loading = true
        const queryParams = 'contentDisposition=inline'
        const upload = await UploadFindOne(attachmentId, queryParams, this.organization)
        window.open(upload.data.url)
      } catch (error) {
        this.snackBar.message = error.message || 'Erro inesperado ao buscar anexo'
        this.snackBar.show = true
      } finally {
        this.loading = false
      }
    },

    closeTicketAttachmentsMenu (e) {
      const ticketsAttachmentMenu = this.$refs.attachmentsDropdownMenu

      if (ticketsAttachmentMenu === e.target) return

      if (this.ticketAttachmentDropdown) this.ticketAttachmentDropdown = false

      window.removeEventListener('click', this.closeTicketAttachmentsMenu)
    },

    toggleAttachmentsDropdownMenu () {
      if (this.ticketAttachmentDropdown) {
        this.ticketAttachmentDropdown = false
        window.removeEventListener('click', this.closeTicketAttachmentsMenu)
        return
      }

      this.ticketAttachmentDropdown = true
      window.addEventListener('click', this.closeTicketAttachmentsMenu)
    }
  }
}
</script>
